package horcrux.frontend.common.views.chat

import arrow.core.None
import arrow.core.some
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.Question
import horcrux.common.utils.eitherWay
import horcrux.common.utils.safelet.safeLet
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.handleResponseException
import horcrux.frontend.common.replace
import io.ktor.http.HttpStatusCode
import io.ktor.util.logging.KtorSimpleLogger
import io.ktor.util.logging.Logger
import kotlinx.coroutines.Job
import mui.material.AlertColor

class PromptSubmitter(
    private val globalAppState: GlobalAppState,
) {
    private val runningJobs = mutableSetOf<Job>()
    private val logger: Logger = KtorSimpleLogger(PromptSubmitter::class.toString())
    private val isLoadingState: BooleanState = globalAppState.submitMessageIconIsLoadingState

    private fun cleanupRunningJobs(shouldCancel: Boolean, isLoadingState: BooleanState) {
        logger.debug("There are ${runningJobs.size} running jobs")
        runningJobs.filter { shouldCancel }.forEach {
            it.cancel()
            logger.debug("Cancelled job $it")
        }
        runningJobs.clear()
        isLoadingState.setFalse()
    }

    fun onCancelRequestAction() {
        globalAppState.inputPromptRef.focus()
        cleanupRunningJobs(true, this.isLoadingState)
    }

    fun onSubmitPromptAction(
        inputPromptMaybeState: MaybeState<InputPrompt>,
        collectionMetadata: CollectionMetadata,
    ) {
        val user = User(this.globalAppState.emailAddressMaybeState.get()?.value)
        globalAppState.inputPromptRef.focus()
        safeLet(
            inputPromptMaybeState.get(),
        ) { inputPrompt ->
            this.submitChatMessageToServerApp(
                chatMessageData = ChatMessageData(inputPrompt, user),
                collectionMetadata = collectionMetadata,
                globalAppState = this.globalAppState,
                cleanupRunningJobs = { cleanupRunningJobs(it, isLoadingState) },
            )?.let {
                runningJobs.add(it)
            }
            inputPromptMaybeState.reset()
        } ?: run {
            this.globalAppState.updateBanner(AlertColor.warning, "Please write a message")
        }
    }

    private fun submitChatMessageToServerApp(
        chatMessageData: ChatMessageData,
        collectionMetadata: CollectionMetadata,
        globalAppState: GlobalAppState,
        cleanupRunningJobs: (Boolean) -> Unit,
    ): Job? {
        val question = Question(chatMessageData.content)
        return this.globalAppState.submitBackendAPIRequestAsync { backendClient ->
            val chatModelConfigs = this@PromptSubmitter.globalAppState.chatModelConfigsState.chatModelConfigs
            isLoadingState.setTrue()
            backendClient
                .askQuestion(collectionMetadata.id, question, chatModelConfigs)
                .onRight { response ->
                    val newChatMessageDataList = globalAppState.currentChatMessageDataList +
                            chatMessageData +
                            ChatMessageData(
                                response.answer.value,
                                TheGrokApp(response.contextDocumentChunkAndMetadataList)
                            )
                    val chatMessageDataListByCollectionID =
                        globalAppState.chatMessageDataListByCollectionIDState.get()
                    chatMessageDataListByCollectionID.replace(collectionMetadata.id, newChatMessageDataList)
                        .let { globalAppState.chatMessageDataListByCollectionIDState.set(it) }
                }.handleResponseException { cause ->
                    when (cause.response.status) {
                        HttpStatusCode.PayloadTooLarge -> {
                            this@PromptSubmitter.globalAppState.updateBanner(
                                AlertColor.error,
                                "Question + context is too large. Try reducing the question length, the max # of relevant document chunks, or selecting a more powerful model."
                            )
                            None
                        }

                        HttpStatusCode.BadGateway -> {
                            this@PromptSubmitter.globalAppState.updateBanner(
                                AlertColor.error,
                                "There was an error sending your question to the chat model. This is likely due to a problem outside of our control. Please try again later."
                            )
                            None
                        }

                        else -> cause.some()
                    }
                }.eitherWay {
                    cleanupRunningJobs(false)
                }.bind()
        }
    }
}
